import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardType, FileText, FileSpreadsheet, Upload, X, RefreshCw } from "lucide-react";
import {
  PortalPageShell,
  PortalPrimaryBtn,
  PortalOutlineBtn,
  PortalGhostBtn,
  RoutingTimeline,
  PortalStatusChip,
} from "@/pages/portal/PortalShared";
import {
  usePortal,
  readRoutingDraftCache,
  writeRoutingDraftCache,
  clearRoutingDraftCache,
} from "@/pages/portal/PortalContext";

const PLACEHOLDER_ROUTING = `2026-09-12 Nashville, TN — Bridgestone Arena
2026-09-13 St. Louis, MO — Enterprise Center
2026-09-15 Chicago, IL — United Center
2026-09-17 Detroit, MI — Little Caesars Arena
2026-09-19 Toronto, ON — Scotiabank Arena`;

/** Very lightweight client-side parser — good enough for the concierge preview.
 *  Server-side re-parses the raw_text authoritatively when staff review it.
 *  Accepted lines: "YYYY-MM-DD City, ST — Venue" or "YYYY-MM-DD City, ST - Venue". */
const parseRoutingText = (text) => {
  const lines = (text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const stops = [];
  const dateRe = /(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/;
  lines.forEach((raw, i) => {
    const dm = raw.match(dateRe);
    if (!dm) return;
    const rest = raw.slice(dm.index + dm[0].length).replace(/^[\s\-—:]+/, "");
    const dashIdx = rest.search(/[\-—]/);
    let cityBlock = rest;
    let venue = "";
    if (dashIdx >= 0) {
      cityBlock = rest.slice(0, dashIdx).trim();
      venue = rest.slice(dashIdx + 1).trim();
    }
    const commaIdx = cityBlock.lastIndexOf(",");
    let city = cityBlock;
    let state = "";
    if (commaIdx >= 0) {
      city = cityBlock.slice(0, commaIdx).trim();
      state = cityBlock.slice(commaIdx + 1).trim();
    }
    stops.push({ sequence: i + 1, date: dm[0], city, state, venue });
  });
  return stops;
};

const PasteModal = ({ open, onClose, onSubmit, initialValue }) => {
  const [value, setValue] = useState(initialValue || "");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { setValue(initialValue || ""); }, [initialValue]);
  if (!open) return null;
  const handleSubmit = async () => {
    if (!value.trim()) {
      toast.error("Please paste at least one line of routing.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(value);
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="portal-paste-modal">
      <button aria-label="Close" className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)" }} onClick={onClose} />
      <div
        className="relative w-full max-w-2xl rounded-md overflow-hidden"
        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border-default)" }}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <div className="text-[15px] font-semibold" style={{ color: "var(--fg-primary)" }}>Paste Routing</div>
            <div className="text-[12px]" style={{ color: "var(--fg-muted)" }}>
              One stop per line — Date, City, ST — Venue
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-md"
                  style={{ color: "var(--fg-muted)" }} data-testid="portal-paste-close"><X size={18} /></button>
        </div>
        <div className="p-5">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={PLACEHOLDER_ROUTING}
            rows={12}
            className="w-full block rounded-md p-3 text-[13.5px] font-mono resize-y"
            style={{
              background: "var(--bg-inset)",
              color: "var(--fg-primary)",
              border: "1px solid var(--border-default)",
              WebkitAppearance: "none",
              minHeight: 260,
            }}
            data-testid="portal-paste-textarea"
          />
        </div>
        <div className="px-5 py-4 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--border-subtle)" }}>
          <PortalGhostBtn onClick={onClose} data-testid="portal-paste-cancel" disabled={submitting}>Cancel</PortalGhostBtn>
          <PortalPrimaryBtn
            onClick={handleSubmit}
            data-testid="portal-paste-submit"
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Submit Routing"}
          </PortalPrimaryBtn>
        </div>
      </div>
    </div>
  );
};

const UploadDropzone = ({ label, description, accept, icon: Icon, onFile, testid }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [fileName, setFileName] = useState("");

  const pick = () => inputRef.current?.click();

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    setFileName(f.name);
    onFile(f);
  };

  return (
    <div
      className="rounded-md p-5 flex flex-col items-start"
      style={{
        background: "var(--bg-surface)",
        border: `1px ${drag ? "solid" : "dashed"} ${drag ? "#1B4F8A" : "var(--border-strong)"}`,
      }}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-1" style={{ color: "var(--fg-primary)" }}>
        <Icon size={18} />
        <div className="text-[14px] font-semibold">{label}</div>
      </div>
      <div className="text-[12.5px] mb-4" style={{ color: "var(--fg-secondary)" }}>{description}</div>
      {fileName && (
        <div className="mb-3 w-full text-[12px] truncate rounded-sm px-2 py-1"
             style={{ background: "var(--bg-inset)", color: "var(--fg-primary)" }}>
          {fileName}
        </div>
      )}
      <button
        onClick={pick}
        type="button"
        className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-[13px] font-semibold"
        style={{ background: "#1B4F8A", color: "#fff" }}
        data-testid={`${testid}-btn`}
      >
        <Upload size={14} /> Select File
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
    </div>
  );
};

const SharedRoutingBlock = ({ shared }) => (
  <div data-testid="portal-routing-shared">
    <div className="flex items-center gap-2 mb-3">
      <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold" style={{ color: "var(--fg-muted)" }}>
        Current Shared Routing
      </div>
      <PortalStatusChip tone="lime">Shared by All Access</PortalStatusChip>
    </div>
    <RoutingTimeline stops={shared.map((s) => ({
      date: s.date, city: s.city, state: s.state, venue: s.venue,
    }))} />
  </div>
);

const SubmittedBlock = ({ submitted }) => (
  <div className="mt-6" data-testid="portal-routing-submitted">
    <div className="flex items-center gap-2 mb-3">
      <div className="uppercase text-[10.5px] tracking-[0.14em] font-semibold" style={{ color: "var(--fg-muted)" }}>
        Your Submitted Routing
      </div>
      <PortalStatusChip tone={submitted.status === "reviewed" ? "lime" : "amber"}>
        {submitted.status === "reviewed" ? "Reviewed" : "Under Review"}
      </PortalStatusChip>
    </div>
    {submitted.stops && submitted.stops.length > 0 ? (
      <RoutingTimeline stops={submitted.stops.map((s) => ({
        date: s.date, city: s.city, state: s.state, venue: s.venue,
      }))} />
    ) : (
      <div
        className="rounded-md p-6"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)" }}
      >
        <div className="text-[14px] font-semibold" style={{ color: "var(--fg-primary)" }}>
          Submission received
        </div>
        <div className="text-[13px] mt-1" style={{ color: "var(--fg-secondary)" }}>
          Your Account Manager will follow up shortly.
        </div>
      </div>
    )}
  </div>
);

export default function PortalRouting() {
  const { activeTour, routing, submitRouting, refreshRouting } = usePortal();
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteInitial, setPasteInitial] = useState("");

  // Rehydrate an in-progress unsaved textarea from local cache (does NOT
  // determine the submitted state — the server does).
  useEffect(() => {
    if (activeTour) {
      const cached = readRoutingDraftCache(activeTour.id);
      if (cached && cached.raw_text) setPasteInitial(cached.raw_text);
    }
  }, [activeTour]);

  if (!activeTour) {
    return (
      <PortalPageShell
        eyebrow="Tour Routing"
        title="Routing"
        subtitle="No active tour on file yet."
        testid="page-portal-routing"
      />
    );
  }

  const submitted = routing?.submitted || null;
  const shared = routing?.shared || [];
  const hasShared = shared.length > 0;
  const hasSubmitted = !!submitted;

  const submitPaste = async (text) => {
    const stops = parseRoutingText(text);
    try {
      // Cache the in-progress text so a network hiccup doesn't lose it.
      writeRoutingDraftCache(activeTour.id, { raw_text: text });
      await submitRouting({ raw_text: text, stops });
      clearRoutingDraftCache(activeTour.id);
      setPasteInitial("");
      setPasteOpen(false);
      toast.success("Routing details submitted successfully. All Access will review shortly.");
    } catch (e) {
      // Server did NOT accept — preserve textarea, tell user to retry.
      // Do NOT show submitted state.
      toast.error("We couldn't send your routing to All Access. Please try again in a moment.");
    }
  };

  const submitFile = async (kind, file) => {
    // Files are staff-processed. Portal cannot upload directly (P0-03).
    // Send a minimal placeholder submission so staff know a file exists;
    // instruct the customer to email the file to their Account Manager.
    try {
      await submitRouting({
        raw_text: `[${kind.toUpperCase()} attachment: ${file.name}] — customer will email attachment to Account Manager.`,
        stops: [],
      });
      toast.success(`We noted your ${kind === "pdf" ? "PDF" : "spreadsheet"} on file. Please email the file to your Account Manager.`);
    } catch (e) {
      toast.error("We couldn't record your submission. Please try again.");
    }
  };

  const resubmit = () => {
    setPasteInitial(submitted?.raw_text || "");
    setPasteOpen(true);
  };

  const showActions = !hasSubmitted;

  return (
    <div className="max-w-[880px] mx-auto" data-testid="page-portal-routing">
      <PortalPageShell
        eyebrow="Tour Routing"
        title="Routing"
        subtitle="Submit or update your tour routing. All Access will review the routing before final pricing and scheduling approval."
        right={
          hasShared || hasSubmitted ? (
            <PortalGhostBtn onClick={refreshRouting} className="!min-w-0"
                            data-testid="portal-routing-refresh">
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </PortalGhostBtn>
          ) : null
        }
      >
        {showActions && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="portal-routing-actions">
            <div
              className="rounded-md p-5 flex flex-col items-start"
              style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)" }}
            >
              <div className="flex items-center gap-2 mb-1" style={{ color: "var(--fg-primary)" }}>
                <ClipboardType size={18} />
                <div className="text-[14px] font-semibold">Paste Routing</div>
              </div>
              <div className="text-[12.5px] mb-4" style={{ color: "var(--fg-secondary)" }}>
                Paste from an email, doc, or spreadsheet.
              </div>
              <PortalPrimaryBtn
                onClick={() => setPasteOpen(true)}
                data-testid="portal-routing-paste-btn"
                className="!min-w-0"
              >
                Paste Routing
              </PortalPrimaryBtn>
            </div>

            <UploadDropzone
              label="Upload PDF"
              description="Booking sheet, itinerary, or advance."
              accept="application/pdf,.pdf"
              icon={FileText}
              onFile={(f) => submitFile("pdf", f)}
              testid="portal-routing-upload-pdf"
            />

            <UploadDropzone
              label="Upload Spreadsheet"
              description=".xlsx or .csv from your production team."
              accept=".xlsx,.xls,.csv"
              icon={FileSpreadsheet}
              onFile={(f) => submitFile("excel", f)}
              testid="portal-routing-upload-xlsx"
            />
          </div>
        )}

        {hasShared && <SharedRoutingBlock shared={shared} />}
        {hasSubmitted && <SubmittedBlock submitted={submitted} />}

        {hasSubmitted && (
          <div className="mt-6">
            <PortalOutlineBtn onClick={resubmit} data-testid="portal-routing-resubmit" className="!min-w-0">
              <RefreshCw size={14} className="mr-1.5" /> Submit an updated routing
            </PortalOutlineBtn>
          </div>
        )}

        {!hasShared && !hasSubmitted && (
          <div className="mt-6 text-[12.5px]" style={{ color: "var(--fg-muted)" }} data-testid="portal-routing-empty-hint">
            Your routing is saved on your All Access account and reviewed by staff before pricing.
          </div>
        )}
      </PortalPageShell>

      <PasteModal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onSubmit={submitPaste}
        initialValue={pasteInitial}
      />
    </div>
  );
}
